// src/roles/store/ReceiveReturns.js

import React, {
    useEffect,
    useState,
    useCallback,
} from "react";

import {
    collection,
    getDocs,
    query,
    where,
} from "firebase/firestore";

import { db } from "../../firebase";

import {
    getPendingReturns,
} from "../../services/assets/assetReplayService";

import {
    receiveReturnedAsset,
} from "../../services/assets/assetTransactionService";

// =====================================================
// HELPERS — no state dependency here
// =====================================================

const formatDateTime = (timestamp) => {
    if (!timestamp) return "-";
    try {
        if (timestamp?.seconds) {
            return new Date(timestamp.seconds * 1000).toLocaleString("en-IN", {
                day:    "2-digit",
                month:  "short",
                year:   "numeric",
                hour:   "2-digit",
                minute: "2-digit",
                hour12: true,
            });
        }
        return "-";
    } catch {
        return "-";
    }
};

const CONDITION_COLORS = {
    GOOD:      { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
    DAMAGED:   { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa" },
    DEFECTIVE: { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
};

// =====================================================
// COMPONENT
// =====================================================

const ReceiveReturns = () => {

    const [transactions, setTransactions]               = useState([]);
    const [users, setUsers]                             = useState([]);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [condition, setCondition]                     = useState("GOOD");
    const [markDefective, setMarkDefective]             = useState(false);
    const [loading, setLoading]                         = useState(false);
    const [pageLoading, setPageLoading]                 = useState(false);
    const [error, setError]                             = useState("");
    const [success, setSuccess]                         = useState("");

    // ──────────────────────────────────────
    // COMPANY / USER
    // ──────────────────────────────────────

    const user            = JSON.parse(localStorage.getItem("user") || "{}");
    const company_id      = localStorage.getItem("company_id") || user?.company_id;
    const currentUserId   = user?.uid || user?.id || "STORE_MANAGER";
    const currentUserName = user?.name || user?.display_name || user?.email?.split("@")[0] || currentUserId;

    // ──────────────────────────────────────
    // RESOLVE HOLDER NAME
    // Inside component — uses users state
    // ──────────────────────────────────────

    const resolveHolderName = (row) => {
        const holderId   = row?.from_id;
        const storedName = row?.from_snapshot?.holder_name || row?.from_holder_name;

        if (holderId) {
            const found = users.find((u) =>
                (u.uid || u.id) === holderId || u.id === holderId
            );
            if (found) {
                return (
                    found.name ||
                    found.display_name ||
                    found.email?.split("@")[0] ||
                    storedName ||
                    holderId
                );
            }
        }

        return storedName || holderId || "N/A";
    };

    // ──────────────────────────────────────
    // LOAD RETURNS + USERS
    // ──────────────────────────────────────

    const loadReturns = useCallback(async () => {
        if (!company_id) { setError("company_id missing"); return; }
        try {
            setPageLoading(true);
            setError("");

            const rows = await getPendingReturns(company_id);
            setTransactions(rows);

            const userSnap = await getDocs(
                query(
                    collection(db, "users"),
                    where("company_id", "==", company_id)
                )
            );
            setUsers(
                userSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
            );

        } catch (err) {
            console.error("LOAD_RETURNS_ERROR", err);
            setError(err?.message || "Failed to load returns");
        } finally {
            setPageLoading(false);
        }
    }, [company_id]);

    useEffect(() => { loadReturns(); }, [loadReturns]);

    // ──────────────────────────────────────
    // RECEIVE RETURN
    // ──────────────────────────────────────

    const handleReceive = async () => {
        if (!selectedTransaction) { setError("Select a return request first"); return; }
        if (!company_id) { setError("company_id missing"); return; }
        try {
            setLoading(true);
            setError("");
            setSuccess("");

            await receiveReturnedAsset({
                transaction_id:   selectedTransaction.id,
                company_id,
                received_by:      currentUserId,
                received_by_name: currentUserName,
                final_condition:  condition,
                mark_defective:   markDefective,
            });

            setSuccess(`Return received — ${selectedTransaction.asset_name || "Asset"} is back in store`);
            setSelectedTransaction(null);
            setCondition("GOOD");
            setMarkDefective(false);
            await loadReturns();

        } catch (err) {
            console.error("RECEIVE_RETURN_ERROR", err);
            setError(err?.message || "Failed to receive return");
        } finally {
            setLoading(false);
        }
    };

    // ──────────────────────────────────────
    // UI
    // ──────────────────────────────────────

    return (
        <div style={{ padding: "24px", background: "#f8fafc", minHeight: "100vh" }}>

            {/* HEADER */}
            <div style={{ marginBottom: "24px" }}>
                <h2 style={{ margin: 0, fontSize: "22px", fontWeight: "700", color: "#111827" }}>
                    Receive Returns
                </h2>
                <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: "14px" }}>
                    Confirm physical return of assets to store
                </p>
            </div>

            {/* ERROR */}
            {error && (
                <div style={{
                    background: "#fef2f2", color: "#dc2626",
                    padding: "12px 16px", borderRadius: "10px",
                    marginBottom: "16px", fontSize: "14px",
                    border: "1px solid #fecaca",
                }}>
                    ⚠ {error}
                </div>
            )}

            {/* SUCCESS */}
            {success && (
                <div style={{
                    background: "#f0fdf4", color: "#16a34a",
                    padding: "12px 16px", borderRadius: "10px",
                    marginBottom: "16px", fontSize: "14px",
                    border: "1px solid #bbf7d0",
                }}>
                    ✓ {success}
                </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", alignItems: "start" }}>

                {/* ─────────────────────────── */}
                {/* LEFT — PENDING RETURNS LIST */}
                {/* ─────────────────────────── */}

                <div>
                    <div style={{
                        background: "#fff", borderRadius: "14px",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                        overflow: "hidden",
                    }}>
                        <div style={{
                            padding: "16px 20px",
                            borderBottom: "1px solid #f3f4f6",
                            display: "flex", alignItems: "center",
                            justifyContent: "space-between",
                        }}>
                            <span style={{ fontWeight: "700", fontSize: "15px", color: "#111827" }}>
                                Pending Returns
                            </span>
                            <span style={{
                                background: "#fef3c7", color: "#d97706",
                                fontSize: "12px", fontWeight: "600",
                                padding: "2px 10px", borderRadius: "999px",
                            }}>
                                {transactions.length} pending
                            </span>
                        </div>

                        {pageLoading ? (
                            <div style={{ padding: "32px", textAlign: "center", color: "#9ca3af", fontSize: "14px" }}>
                                Loading...
                            </div>
                        ) : transactions.length === 0 ? (
                            <div style={{ padding: "40px", textAlign: "center" }}>
                                <div style={{ fontSize: "28px", marginBottom: "8px" }}>📭</div>
                                <div style={{ color: "#374151", fontWeight: "600" }}>No pending returns</div>
                                <div style={{ color: "#9ca3af", fontSize: "13px", marginTop: "4px" }}>
                                    All returns have been processed
                                </div>
                            </div>
                        ) : (
                            <div>
                                {transactions.map((txn) => {
                                    const isSelected = selectedTransaction?.id === txn.id;
                                    const holderName = resolveHolderName(txn);
                                    return (
                                        <div
                                            key={txn.id}
                                            onClick={() => setSelectedTransaction(isSelected ? null : txn)}
                                            style={{
                                                padding: "16px 20px",
                                                borderBottom: "1px solid #f9fafb",
                                                cursor: "pointer",
                                                background: isSelected ? "#eff6ff" : "#fff",
                                                borderLeft: isSelected ? "3px solid #2563eb" : "3px solid transparent",
                                                transition: "background 0.15s",
                                            }}
                                        >
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                                <div>
                                                    <div style={{ fontWeight: "700", fontSize: "14px", color: "#111827" }}>
                                                        {txn.asset_name || "Unnamed Asset"}
                                                    </div>
                                                    <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
                                                        # {txn.serial_no || "—"}
                                                    </div>
                                                </div>
                                                {isSelected && (
                                                    <span style={{
                                                        background: "#2563eb", color: "#fff",
                                                        fontSize: "11px", fontWeight: "600",
                                                        padding: "2px 8px", borderRadius: "999px",
                                                    }}>
                                                        Selected
                                                    </span>
                                                )}
                                            </div>

                                            {/* RETURN INFO */}
                                            <div style={{
                                                marginTop: "10px", display: "grid",
                                                gridTemplateColumns: "1fr 1fr",
                                                gap: "8px",
                                            }}>
                                                <InfoChip label="Returned By" value={holderName} />
                                                <InfoChip label="Holder Type" value={txn.from_type || "-"} />
                                                <InfoChip
                                                    label="Requested By"
                                                    value={txn.requested_by_name || txn.requested_by || "-"}
                                                />
                                                <InfoChip
                                                    label="Return Date"
                                                    value={formatDateTime(txn.created_at)}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* ─────────────────────────── */}
                {/* RIGHT — RECEIVE PANEL       */}
                {/* ─────────────────────────── */}

                <div>
                    {!selectedTransaction ? (
                        <div style={{
                            background: "#fff", borderRadius: "14px",
                            padding: "40px", textAlign: "center",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                            border: "2px dashed #e5e7eb",
                        }}>
                            <div style={{ fontSize: "32px", marginBottom: "12px" }}>👈</div>
                            <div style={{ color: "#374151", fontWeight: "600", fontSize: "15px" }}>
                                Select a return request
                            </div>
                            <div style={{ color: "#9ca3af", fontSize: "13px", marginTop: "4px" }}>
                                Click on any item from the list to begin
                            </div>
                        </div>
                    ) : (
                        <div style={{
                            background: "#fff", borderRadius: "14px",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                            overflow: "hidden",
                        }}>
                            {/* PANEL HEADER */}
                            <div style={{
                                padding: "16px 20px",
                                borderBottom: "1px solid #f3f4f6",
                                background: "#fafafa",
                            }}>
                                <div style={{ fontWeight: "700", fontSize: "15px", color: "#111827" }}>
                                    Receive Asset
                                </div>
                                <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>
                                    Confirm physical receipt and condition
                                </div>
                            </div>

                            <div style={{ padding: "20px" }}>

                                {/* ASSET SUMMARY CARD */}
                                <div style={{
                                    background: "#f8fafc", borderRadius: "10px",
                                    padding: "16px", marginBottom: "20px",
                                    border: "1px solid #e5e7eb",
                                }}>
                                    <div style={{ fontWeight: "700", fontSize: "16px", color: "#111827" }}>
                                        {selectedTransaction.asset_name || "Asset"}
                                    </div>
                                    <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>
                                        Serial: {selectedTransaction.serial_no || "—"}
                                    </div>
                                    <div style={{
                                        marginTop: "12px", display: "grid",
                                        gridTemplateColumns: "1fr 1fr", gap: "10px",
                                    }}>
                                        <InfoChip
                                            label="Returned By"
                                            value={resolveHolderName(selectedTransaction)}
                                        />
                                        <InfoChip
                                            label="Holder Type"
                                            value={selectedTransaction.from_type || "-"}
                                        />
                                        <InfoChip
                                            label="Requested By"
                                            value={selectedTransaction.requested_by_name || selectedTransaction.requested_by || "-"}
                                        />
                                        <InfoChip
                                            label="Return Submitted"
                                            value={formatDateTime(selectedTransaction.created_at)}
                                        />
                                    </div>
                                </div>

                                {/* CONDITION */}
                                <div style={{ marginBottom: "16px" }}>
                                    <label style={{
                                        fontSize: "13px", fontWeight: "600",
                                        color: "#374151", display: "block", marginBottom: "8px",
                                    }}>
                                        Final Condition
                                    </label>
                                    <div style={{ display: "flex", gap: "8px" }}>
                                        {["GOOD", "DAMAGED", "DEFECTIVE"].map((c) => {
                                            const s      = CONDITION_COLORS[c];
                                            const active = condition === c;
                                            return (
                                                <button
                                                    key={c}
                                                    onClick={() => setCondition(c)}
                                                    disabled={loading}
                                                    style={{
                                                        flex: 1, padding: "10px",
                                                        borderRadius: "8px", border: "2px solid",
                                                        borderColor: active ? s.color : "#e5e7eb",
                                                        background:  active ? s.bg   : "#fff",
                                                        color:       active ? s.color : "#6b7280",
                                                        fontWeight:  active ? "700"  : "500",
                                                        fontSize: "13px", cursor: "pointer",
                                                        transition: "all 0.15s",
                                                    }}
                                                >
                                                    {c === "GOOD" ? "✓ Good" : c === "DAMAGED" ? "⚠ Damaged" : "✗ Defective"}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* MARK DEFECTIVE */}
                                <div style={{ marginBottom: "20px" }}>
                                    <label style={{
                                        display: "flex", alignItems: "center", gap: "10px",
                                        cursor: "pointer", padding: "12px",
                                        borderRadius: "8px", border: "1px solid",
                                        borderColor: markDefective ? "#fca5a5" : "#e5e7eb",
                                        background:  markDefective ? "#fef2f2" : "#fafafa",
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={markDefective}
                                            onChange={(e) => setMarkDefective(e.target.checked)}
                                            disabled={loading}
                                            style={{ width: "16px", height: "16px" }}
                                        />
                                        <div>
                                            <div style={{
                                                fontWeight: "600", fontSize: "13px",
                                                color: markDefective ? "#dc2626" : "#374151",
                                            }}>
                                                Mark As Defective
                                            </div>
                                            <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "1px" }}>
                                                Asset will be moved to defective stock
                                            </div>
                                        </div>
                                    </label>
                                </div>

                                {/* RECEIVE BUTTON */}
                                <button
                                    onClick={handleReceive}
                                    disabled={loading || pageLoading}
                                    style={{
                                        width: "100%", padding: "13px",
                                        borderRadius: "10px", border: "none",
                                        background: loading ? "#93c5fd" : "#1e40af",
                                        color: "#fff", fontWeight: "700",
                                        fontSize: "15px",
                                        cursor: loading ? "not-allowed" : "pointer",
                                        transition: "background 0.2s",
                                    }}
                                >
                                    {loading ? "Receiving..." : "✓ Confirm Receipt"}
                                </button>

                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// =====================================================
// INFO CHIP
// =====================================================

function InfoChip({ label, value }) {
    return (
        <div>
            <div style={{
                fontSize: "11px", color: "#9ca3af", fontWeight: "500",
                textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "2px",
            }}>
                {label}
            </div>
            <div style={{ fontSize: "13px", fontWeight: "600", color: "#374151" }}>
                {value || "-"}
            </div>
        </div>
    );
}

export default ReceiveReturns;